import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowDown, ArrowRight, ArrowUp, Check, Copy, Link2, Pencil, Play, Plus, Trash2, Unlink2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { EstadoVacio, Seccion } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { NumberField, TextField } from '@/components/Field';
import {
  alternarBiserieConSiguiente,
  borrarRutina,
  duplicarRutina,
  guardarDatosDeRutina,
  guardarEjercicioDeRutina,
  leerRutina,
  moverEjercicioDeRutina,
  quitarEjercicioDeRutina,
  type EjercicioDeRutina,
} from '@/db/rutinas';
import { iniciarEntrenamiento, leerEnCurso } from '@/db/entrenamientos';
import { mensajeDeError } from '@/db/tx';
import type { Routine } from '@/db/types';
import { textoDescanso, textoObjetivo } from '@/domain/entrenamiento';
import { fechaCorta } from '@/domain/hoy';
import { DIAS_SEMANA, textoDias } from '@/domain/sesiones';
import { leerDecimal } from '@/domain/numbers';
import { ETIQUETAS_MODALIDAD, MODALIDADES, modalidadDe, textoPausaYVariante } from '@/domain/modalidad';
import { esDeCiclo } from '@/datos/rutinas-precargadas';
import { CompartirRutina } from './CompartirRutina';
import { Segmentado } from '@/components/Segmentado';
import type { Modalidad } from '@/db/types';

/** Una rutina: ver, empezar y editar (Req. 10). */
export function RutinaPage() {
  const { id = '' } = useParams();
  const [parametros, setParametros] = useSearchParams();
  const navegar = useNavigate();
  const detalle = useLiveQuery(() => leerRutina(id), [id]);
  const enCurso = useLiveQuery(() => leerEnCurso(), []);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const editando = parametros.get('editar') === '1';

  if (detalle === undefined || enCurso === undefined) return null;
  if (detalle === null) {
    return (
      <div>
        <PageHeader titulo="No está." volver={{ a: '/gym', texto: 'Gym' }} />
        <EstadoVacio titulo="Esa rutina ya no existe." descripcion="Los entrenamientos que hiciste con ella se conservan." />
      </div>
    );
  }
  const { rutina, ejercicios, ultimaVez } = detalle;

  async function empezar() {
    if (ocupado) return;
    setOcupado(true);
    setError(null);
    try {
      await iniciarEntrenamiento(rutina.id);
      void navegar('/gym/entrenamiento');
    } catch (e) {
      setError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo={[
          ETIQUETAS_MODALIDAD[modalidadDe(rutina)],
          textoDias(rutina.dias) || (esDeCiclo(rutina.plantilla) ? 'Días variables' : ''),
          ultimaVez === null ? 'Sin hacer todavía' : `Última vez: ${fechaCorta(ultimaVez)}`,
        ]
          .filter((x) => x !== '')
          .join(' · ')}
        titulo={rutina.nombre}
        volver={{ a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' }}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}

      {!editando &&
        (enCurso === null ? (
          <Button icono={Play} tamano="grande" disabled={ocupado || ejercicios.length === 0} onClick={() => void empezar()}>
            Empezar
          </Button>
        ) : (
          <BotonEnlace to="/gym/entrenamiento" icono={ArrowRight} tamano="grande">
            {enCurso.routineId === rutina.id ? 'Continuar entrenamiento' : 'Tienes otro en curso: continuar'}
          </BotonEnlace>
        ))}

      {editando ? (
        <DatosRutina rutina={rutina} onError={setError} />
      ) : (
        rutina.notas !== null && <p className="text-[13px] leading-relaxed text-texto-suave">{rutina.notas}</p>
      )}

      <Seccion titulo="Ejercicios" extra={ejercicios.length === 1 ? '1 ejercicio' : `${ejercicios.length} ejercicios`}>
        {ejercicios.length === 0 ? (
          <p className="text-[13px] text-texto-suave">Agrega el primer ejercicio.</p>
        ) : (
          <ul className="divide-y divide-borde">
            {ejercicios.map((e, i) =>
              editando ? (
                <EjercicioEditable
                  key={e.item.id}
                  e={e}
                  casa={modalidadDe(rutina) === 'casa'}
                  primero={i === 0}
                  ultimo={i === ejercicios.length - 1}
                  conSiguiente={(e.item.biserie ?? null) !== null && ejercicios[i + 1]?.item.biserie === e.item.biserie}
                  onAccion={(fn) => void accion(fn)}
                />
              ) : (
                <EjercicioDeLista key={e.item.id} e={e} siguiente={ejercicios[i + 1]} anterior={ejercicios[i - 1]} />
              ),
            )}
          </ul>
        )}
        {editando && (
          <BotonEnlace to={`/gym/ejercicios?para=rutina:${rutina.id}`} variante="secundario" icono={Plus}>
            Agregar ejercicio
          </BotonEnlace>
        )}
      </Seccion>

      <div className="grid gap-2">
        {editando ? (
          <Button icono={Check} onClick={() => setParametros({}, { replace: true })}>
            Listo
          </Button>
        ) : (
          <Button variante="secundario" icono={Pencil} onClick={() => setParametros({ editar: '1' }, { replace: true })}>
            Editar rutina
          </Button>
        )}
        <Button
          variante="fantasma"
          icono={Copy}
          onClick={() =>
            void accion(async () => {
              const nueva = await duplicarRutina(rutina.id);
              void navegar(`/gym/rutina/${nueva}?editar=1`);
            })
          }
        >
          Duplicar
        </Button>
        <CompartirRutina routineId={rutina.id} />
        {confirmarBorrar ? (
          <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
            <p className="text-[13px] text-texto">¿Borrar esta rutina? Los entrenamientos que hiciste con ella se conservan.</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variante="peligro"
                onClick={() =>
                  void accion(async () => {
                    await borrarRutina(rutina.id);
                    void navegar('/gym', { replace: true });
                  })
                }
              >
                Sí, borrar
              </Button>
              <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
            Borrar rutina
          </Button>
        )}
      </div>
    </div>
  );
}

function EjercicioDeLista({
  e,
  siguiente,
  anterior,
}: {
  e: EjercicioDeRutina;
  siguiente: EjercicioDeRutina | undefined;
  anterior: EjercicioDeRutina | undefined;
}) {
  const clave = e.item.biserie ?? null;
  const conSiguiente = clave !== null && siguiente?.item.biserie === clave;
  const conAnterior = clave !== null && anterior?.item.biserie === clave;
  return (
    <li className="py-3">
      {clave !== null && !conAnterior && <p className="antetitulo mb-1">Biserie: alterna los dos</p>}
      <p className="flex items-baseline justify-between gap-3">
        <span className="text-[14px] font-medium text-texto">{e.ejercicio?.nombre ?? 'Ejercicio que ya no existe'}</span>
        <span className="whitespace-nowrap text-[13px] tabular-nums text-texto">
          {textoObjetivo(e.item.series, e.item.repsMin, e.item.repsMax, e.item.porLado === true)}
        </span>
      </p>
      <p className="text-[12px] text-texto-suave">
        {clave === null
          ? `Descanso ${textoDescanso(e.item.descansoSeg)}`
          : conSiguiente
            ? 'Sigue el siguiente sin descanso'
            : `Descanso al terminar los dos: ${textoDescanso(e.item.descansoBiserieSeg ?? e.item.descansoSeg)}`}
      </p>
      {textoPausaYVariante(e.item) !== '' && <p className="text-[12px] text-texto-suave">{mayuscula(textoPausaYVariante(e.item))}</p>}
      {e.item.notas !== null && <p className="mt-1 text-[12px] leading-relaxed text-texto-suave">{e.item.notas}</p>}
    </li>
  );
}

const mayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);

function DatosRutina({ rutina, onError }: { rutina: Routine; onError: (e: string | null) => void }) {
  const [nombre, setNombre] = useState(rutina.nombre);
  const [notas, setNotas] = useState(rutina.notas ?? '');
  const [dias, setDias] = useState<number[]>(rutina.dias ?? []);
  const [modalidad, setModalidad] = useState<Modalidad>(modalidadDe(rutina));
  const [listo, setListo] = useState(false);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    onError(null);
    setListo(false);
    try {
      await guardarDatosDeRutina(rutina.id, { nombre, notas, dias, modalidad });
      setListo(true);
    } catch (e) {
      onError(mensajeDeError(e));
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
      <TextField etiqueta="Nombre de la rutina" valor={nombre} onCambio={setNombre} maxLength={60} />
      <div>
        <Segmentado
          etiqueta="Modalidad de la rutina"
          etiquetaVisible
          completo
          valor={modalidad}
          onCambio={setModalidad}
          opciones={MODALIDADES.map((m) => ({ valor: m, etiqueta: ETIQUETAS_MODALIDAD[m] }))}
        />
        <p className="mt-1.5 text-[12px] leading-relaxed text-texto-suave">
          Solo cambia en qué lista aparece. Sus entrenamientos conservan la modalidad en que se hicieron.
        </p>
      </div>
      <fieldset>
        <legend className="mb-2 text-[13px] text-texto-suave">Días en que toca</legend>
        <div className="grid grid-cols-7 gap-1.5">
          {DIAS_SEMANA.map((d) => {
            const activo = dias.includes(d.dia);
            return (
              <button
                key={d.dia}
                type="button"
                aria-pressed={activo}
                aria-label={d.nombre}
                onClick={() => setDias((x) => (activo ? x.filter((y) => y !== d.dia) : [...x, d.dia]))}
                className={`min-h-11 rounded-[13px] text-[13px] font-medium ${activo ? 'bg-marca text-sobre-marca' : 'bg-superficie text-texto'}`}
              >
                {d.corto}
              </button>
            );
          })}
        </div>
      </fieldset>
      <TextField etiqueta="Notas (opcional)" valor={notas} onCambio={setNotas} maxLength={600} />
      {listo && <Banner tono="exito" titulo="Guardado" />}
      <Button type="submit" variante="secundario" icono={Check}>
        Guardar nombre, modalidad, días y notas
      </Button>
    </form>
  );
}

function EjercicioEditable({
  e,
  casa,
  primero,
  ultimo,
  conSiguiente,
  onAccion,
}: {
  e: EjercicioDeRutina;
  /** En las rutinas de casa también se anotan la pausa y la variante. */
  casa: boolean;
  primero: boolean;
  ultimo: boolean;
  conSiguiente: boolean;
  onAccion: (fn: () => Promise<unknown>) => void;
}) {
  const texto = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  const [series, setSeries] = useState(texto(e.item.series));
  const [repsMin, setRepsMin] = useState(texto(e.item.repsMin));
  const [repsMax, setRepsMax] = useState(texto(e.item.repsMax));
  const [descanso, setDescanso] = useState(texto(e.item.descansoSeg));
  const [descansoBiserie, setDescansoBiserie] = useState(texto(e.item.descansoBiserieSeg));
  const [pausa, setPausa] = useState(texto(e.item.pausaSeg));
  const [variante, setVariante] = useState(e.item.variante ?? '');
  const [porLado, setPorLado] = useState(e.item.porLado === true);
  const [notas, setNotas] = useState(e.item.notas ?? '');
  const [guardado, setGuardado] = useState(false);
  const enBiserie = (e.item.biserie ?? null) !== null;
  const sinReps = e.ejercicio?.tipo === 'tiempo' || e.ejercicio?.tipo === 'distancia';
  // En casa se elige; si ya venía por lado (una rutina que se pasó al gimnasio), también se puede quitar
  const conPorLado = !sinReps && (casa || e.item.porLado === true);
  const entero = (t: string) => (t.trim() === '' ? null : leerDecimal(t));

  return (
    <li className="space-y-3 py-4">
      <p className="flex items-center justify-between gap-2">
        <span className="text-[14px] font-medium text-texto">{e.ejercicio?.nombre ?? 'Ejercicio que ya no existe'}</span>
        {enBiserie && <span className="text-[12px] text-marca">Biserie {e.item.biserie}</span>}
      </p>
      <div className="grid grid-cols-3 gap-2">
        <NumberField etiqueta="Series" valor={series} onCambio={setSeries} decimal={false} />
        {!sinReps && <NumberField etiqueta="Reps mín." valor={repsMin} onCambio={setRepsMin} decimal={false} />}
        {!sinReps && <NumberField etiqueta="Reps máx." valor={repsMax} onCambio={setRepsMax} decimal={false} />}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumberField etiqueta={enBiserie ? 'Descanso por separado (s)' : 'Descanso (s)'} valor={descanso} onCambio={setDescanso} decimal={false} />
        {enBiserie && (
          <NumberField etiqueta="Descanso de la biserie (s)" valor={descansoBiserie} onCambio={setDescansoBiserie} decimal={false} />
        )}
      </div>
      {casa && (
        <div className="grid grid-cols-2 gap-2">
          <TextField etiqueta="Variante (opcional)" valor={variante} onCambio={setVariante} maxLength={60} />
          <NumberField etiqueta="Pausa por rep. (s)" valor={pausa} onCambio={setPausa} decimal={false} />
        </div>
      )}
      {conPorLado && (
        <Casilla marcada={porLado} onCambio={setPorLado}>
          Repeticiones por lado: los dos lados son una serie
        </Casilla>
      )}
      <TextField etiqueta="Notas del ejercicio" valor={notas} onCambio={setNotas} maxLength={300} />
      {guardado && <p className="text-[12px] text-exito">Guardado.</p>}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variante="secundario"
          icono={Check}
          onClick={() =>
            onAccion(async () => {
              await guardarEjercicioDeRutina({
                ...e.item,
                series: entero(series) ?? 0,
                repsMin: sinReps ? null : entero(repsMin),
                repsMax: sinReps ? null : entero(repsMax),
                descansoSeg: entero(descanso) ?? 0,
                descansoBiserieSeg: enBiserie ? entero(descansoBiserie) : null,
                ...(casa ? { pausaSeg: entero(pausa), variante } : {}),
                ...(conPorLado ? { porLado } : {}),
                notas,
              });
              setGuardado(true);
            })
          }
        >
          Guardar
        </Button>
        <Button variante="fantasma" icono={Trash2} onClick={() => onAccion(() => quitarEjercicioDeRutina(e.item.id))}>
          Quitar
        </Button>
        <Button variante="fantasma" icono={ArrowUp} disabled={primero} onClick={() => onAccion(() => moverEjercicioDeRutina(e.item.id, -1))}>
          Subir
        </Button>
        <Button variante="fantasma" icono={ArrowDown} disabled={ultimo} onClick={() => onAccion(() => moverEjercicioDeRutina(e.item.id, 1))}>
          Bajar
        </Button>
      </div>
      {!ultimo && (
        <Button
          variante="fantasma"
          icono={conSiguiente ? Unlink2 : Link2}
          onClick={() => onAccion(() => alternarBiserieConSiguiente(e.item.id, Math.max(e.item.descansoSeg, 60)))}
        >
          {conSiguiente ? 'Separar la biserie' : 'Biserie con el siguiente'}
        </Button>
      )}
    </li>
  );
}
